import { motion } from "framer-motion";
import GrailedImage from "../images/grailed.svg";
import DepopImage from "../images/logos/depop.svg";
import PoizonImage from "../images/logos/poizon.png";
import MercariImage from "../images/logos/mercari.png";
import AmazonImage from "../images/logos/amazon.png";
import PoshmarkImage from "../images/logos/poshmark.png";
import StockXImage from "../images/logos/stockx.png";
import ShopifyImage from "../images/logos/shopify.png";
import EbayImage from "../images/logos/ebay.png";
import FacebookImage from "../images/logos/facebook.png";

const platforms = [
  {
    name: "Mercari",
    src: MercariImage,
  },
  { name: "Grailed", src: GrailedImage },
  {
    name: "Amazon",
    src: AmazonImage,
  },
  {
    name: "Poshmark",
    src: PoshmarkImage,
  },
  {
    name: "Stock X",
    src: StockXImage,
  },
  {
    name: "Depop",
    src: DepopImage,
  },
  {
    name: "Shopify",
    src: ShopifyImage,
  },
  {
    name: "eBay",
    src: EbayImage,
  },
  {
    name: "Facebook",
    src: FacebookImage,
  },
  {
    name: "Poizon",
    src: PoizonImage,
  },
];

const PoweredBy: React.FC = () => {
  // Duplicate platforms for seamless loop
  const duplicatedPlatforms = [...platforms, ...platforms];

  return (
    <section className="relative  bg-white dark:bg-gray-800 py-10 sm:py-20 sm:pt-28 overflow-hidden w-full">
      <div className="w-full">
        <motion.div
          className="mx-auto max-w-3xl text-center mb-8 sm:mb-16 px-6 lg:px-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
        >
          <motion.p
            className="text-base sm:text-xl font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Powering Sellers Across Multiple Platforms
          </motion.p>
        </motion.div>

        {/* Gradient overlays for fade effect */}
        <div className="relative w-full">
          <div className="absolute left-0 top-0 bottom-0 w-10 sm:w-40 bg-gradient-to-r from-white via-gray-50/50 to-transparent dark:from-gray-800 dark:via-gray-800 dark:to-transparent z-20 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-10 sm:w-40 bg-gradient-to-l from-white via-gray-50/50 to-transparent dark:from-gray-800 dark:via-gray-800 dark:to-transparent z-20 pointer-events-none" />

          {/* Scrolling container */}
          <div className="flex overflow-x-hidden overflow-y-visible pt-0 sm:pt-8 pb-0 sm:pb-16 w-full">
            <motion.div
              className="flex items-center gap-6 md:gap-8"
              animate={{
                x: ["0%", "-50%"], // Scroll exactly half (one set of platforms) for seamless loop
              }}
              transition={{
                x: {
                  repeat: Infinity,
                  repeatType: "loop",
                  duration: 30,
                  ease: "linear",
                },
              }}
            >
              {duplicatedPlatforms.map((platform, index) => (
                <motion.div
                  key={`${platform.name}-${index}`}
                  className="group relative flex items-center justify-center flex-shrink-0 min-w-[120px] sm:min-w-[160px] md:min-w-[180px] h-16 sm:h-20 md:h-24 px-4 sm:px-8 py-2 sm:py-4 bg-white dark:bg-gray-800 rounded-2xl shadow-md dark:shadow-xl border border-gray-200/80 dark:border-gray-700/50 transition-all duration-500 backdrop-blur-sm overflow-hidden"
                  whileHover={{
                    scale: 1.05,
                    y: -4,
                    boxShadow: "0 20px 40px rgba(99, 102, 241, 0.2)",
                    borderColor: "rgba(99, 102, 241, 0.4)",
                  }}
                  initial={{ opacity: 0.9 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Gradient overlay on hover */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-br from-softBlue/5 via-brightPurple/5 to-deepPurple/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    initial={false}
                  />

                  {/* Decorative corner accent */}
                  <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-softBlue/10 via-brightPurple/10 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  {/* Bottom border accent on hover */}
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-softBlue via-brightPurple to-deepPurple scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-center"
                    initial={false}
                  />

                  <img
                    src={platform.src}
                    alt={platform.name}
                    className="relative z-10 h-8 md:h-10 w-full max-w-[120px] md:max-w-[140px] object-contain grayscale group-hover:grayscale-0 transition-all duration-500 filter brightness-95 dark:brightness-110 dark:contrast-105 group-hover:brightness-100 group-hover:scale-105"
                  />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Subtle background decoration */}
        <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
          <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-gradient-to-br from-softBlue/5 to-transparent rounded-full blur-3xl" />
          <div className="absolute top-1/2 right-1/4 w-96 h-96 bg-gradient-to-br from-brightPurple/5 to-transparent rounded-full blur-3xl" />
        </div>
      </div>
    </section>
  );
};

export default PoweredBy;
